import types from './types';
import ConversationsAPI from '../../../api/internalChat/conversations';
import MessagesAPI from '../../../api/internalChat/messages';

const actions = {
  fetchConversations: async ({ commit }) => {
    commit(types.SET_INTERNAL_CHAT_UI_FLAG, { isFetchingConversations: true });
    try {
      const { data } = await ConversationsAPI.get();
      commit(types.SET_INTERNAL_CHAT_CONVERSATIONS, data.payload);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_INTERNAL_CHAT_UI_FLAG, {
        isFetchingConversations: false,
      });
    }
  },

  startConversation: async ({ commit }, userId) => {
    commit(types.SET_INTERNAL_CHAT_UI_FLAG, { isCreatingConversation: true });
    try {
      const { data } = await ConversationsAPI.create({ user_id: userId });
      commit(types.UPSERT_INTERNAL_CHAT_CONVERSATION, data);
      return data;
    } finally {
      commit(types.SET_INTERNAL_CHAT_UI_FLAG, {
        isCreatingConversation: false,
      });
    }
  },

  fetchConversation: async ({ commit }, conversationId) => {
    const { data } = await ConversationsAPI.show(conversationId);
    commit(types.UPSERT_INTERNAL_CHAT_CONVERSATION, data);
    return data;
  },

  fetchMessages: async ({ commit }, { conversationId, before }) => {
    commit(types.SET_INTERNAL_CHAT_UI_FLAG, { isFetchingMessages: true });
    try {
      const { data } = await MessagesAPI.get(conversationId, before);
      if (before) {
        commit(types.PREPEND_INTERNAL_CHAT_MESSAGES, {
          conversationId,
          messages: data.payload,
        });
      } else {
        commit(types.SET_INTERNAL_CHAT_MESSAGES, {
          conversationId,
          messages: data.payload,
        });
      }
      return data.payload;
    } finally {
      commit(types.SET_INTERNAL_CHAT_UI_FLAG, { isFetchingMessages: false });
    }
  },

  sendMessage: async ({ commit }, { conversationId, content, files }) => {
    commit(types.SET_INTERNAL_CHAT_UI_FLAG, { isSendingMessage: true });
    try {
      const { data } = await MessagesAPI.create(conversationId, {
        content,
        files,
      });
      commit(types.ADD_INTERNAL_CHAT_MESSAGE, {
        conversationId,
        message: data,
      });
      commit(types.UPDATE_INTERNAL_CHAT_CONVERSATION_ACTIVITY, {
        conversationId,
        lastActivityAt: data.created_at,
      });
      return data;
    } finally {
      commit(types.SET_INTERNAL_CHAT_UI_FLAG, { isSendingMessage: false });
    }
  },

  markConversationDelivered: async (_, conversationId) => {
    try {
      await ConversationsAPI.markDelivered(conversationId);
    } catch (error) {
      // Ignore error, this is best-effort
    }
  },

  markConversationRead: async ({ commit }, conversationId) => {
    try {
      await ConversationsAPI.markRead(conversationId);
      commit(types.SET_INTERNAL_CHAT_CONVERSATION_UNREAD_COUNT, {
        conversationId,
        unreadCount: 0,
      });
    } catch (error) {
      // Ignore error, this is best-effort
    }
  },

  setActiveConversationId: ({ commit }, conversationId) => {
    commit(types.SET_INTERNAL_CHAT_ACTIVE_CONVERSATION_ID, conversationId);
  },

  clearActiveConversationId: ({ commit }) => {
    commit(types.CLEAR_INTERNAL_CHAT_ACTIVE_CONVERSATION_ID);
  },

  handleIncomingMessage: (
    { commit, state, dispatch, rootGetters },
    { internal_conversation_id: conversationId, message }
  ) => {
    commit(types.ADD_INTERNAL_CHAT_MESSAGE, { conversationId, message });
    commit(types.UPDATE_INTERNAL_CHAT_CONVERSATION_ACTIVITY, {
      conversationId,
      lastActivityAt: message.created_at,
    });

    const currentUserId = rootGetters.getCurrentUserID;
    if (message.sender_id === currentUserId) {
      return;
    }

    if (Number(state.activeConversationId) === Number(conversationId)) {
      dispatch('markConversationRead', conversationId);
      dispatch('markConversationDelivered', conversationId);
    } else {
      commit(types.INCREMENT_INTERNAL_CHAT_UNREAD_COUNT, conversationId);
    }
  },

  applyReadReceipt: (
    { commit },
    {
      internal_conversation_id: conversationId,
      reader_id: readerId,
      read_at: readAt,
    }
  ) => {
    commit(types.MARK_INTERNAL_CHAT_MESSAGES_READ, {
      conversationId,
      readerId,
      readAt,
    });
  },
};

export default actions;
